import { useEffect, useState } from "react";
import { getAppStoreDeepLink, buildAppStoreUrl, isIOS } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";

/**
 * Short-link redirector for social bios and campaigns.
 *
 * URL pattern: https://stableish.me/ig, /tw, /rd, /nl, /ig-story, /fb
 *
 * Design:
 * Instagram / Facebook / TikTok in-app browsers on iOS aggressively block
 * programmatic navigation (window.location.href / .replace) even for
 * apps.apple.com and itms-apps:// URLs. The reliable path is to have the
 * user tap a real <a href> — user-initiated navigation is respected.
 *
 * So this page shows a BIG, obvious "Continue to App Store" button as the
 * primary action, and *also* attempts an auto-redirect in the background
 * for direct-Safari visitors where it works.
 *
 * If the browser is detected as an in-app browser (IG / FB / TikTok / X /
 * LinkedIn / Snapchat), a hint appears telling the user how to escape to
 * external Safari for the smoothest experience.
 */

function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Instagram|FBAN|FBAV|Messenger|TikTok|Twitter|Line\/|LinkedInApp|Snapchat/i.test(
    ua
  );
}

export default function Redirect({ source, medium, label, ct }) {
  // Compute once on mount (isIOS + isInAppBrowser are client-only).
  const [deepLink] = useState(() => getAppStoreDeepLink(ct || label));
  const [webLink] = useState(() => buildAppStoreUrl(ct || label));
  const [inApp, setInApp] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    setInApp(isInAppBrowser());
    setIos(isIOS());

    trackEvent("download_click", "short-link", {
      store: "apple",
      utm_source: source,
      utm_medium: medium,
      short: label,
      ct: ct || label,
    });

    // Attempt auto-redirect for direct Safari / desktop visitors. In-app
    // browsers silently block this — that's fine; the button below is the
    // real UX for them.
    const t = setTimeout(() => {
      try {
        window.location.href = deepLink;
      } catch {
        /* IG's browser sometimes throws — ignore, user taps button */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [source, medium, label, ct, deepLink]);

  const handleContinueClick = () => {
    trackEvent("download_click", "short-link-manual", {
      store: "apple",
      utm_source: source,
      utm_medium: medium,
      short: label,
      ct: ct || label,
    });
  };

  return (
    <main
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-16 noise"
      data-testid={`redirect-${label}`}
    >
      <div className="text-center w-full max-w-sm">
        <div
          className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-5"
          data-testid="redirect-tagline"
        >
          // continue to the app store
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-none">
          Stable<span className="text-[#FF2A2A]">-ish</span>
        </h1>
        <p className="mt-3 text-sm text-white/55">
          14-day free trial · $9.99/mo or $79.99/yr
        </p>

        {/* Primary CTA — a real anchor, so a user tap counts as a
            user-initiated navigation and Instagram / Facebook / TikTok
            in-app browsers respect it. On iOS we use the itms-apps://
            deep link (which hands off to the App Store app directly). */}
        <a
          href={deepLink}
          onClick={handleContinueClick}
          className="mt-8 inline-flex items-center justify-center w-full bg-[#FF2A2A] hover:bg-[#e02020] text-white font-display font-bold text-lg tracking-tight px-8 py-4 transition-colors"
          data-testid="redirect-continue-button"
        >
          Continue to App Store →
        </a>

        {/* Secondary explicit web link — for the rare case where the deep
            link fails (e.g. user is on Android / desktop, or iOS App Store
            app is somehow missing). Uses target=_blank + rel=external to
            break out of any in-app WebView. */}
        <a
          href={webLink}
          target="_blank"
          rel="noopener noreferrer external"
          className="mt-4 inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-[#FF2A2A] underline"
          data-testid="redirect-fallback-link"
        >
          open in browser instead
        </a>

        {/* Escape-hatch hint for in-app browsers where even the button
            handoff may be flaky. Only shown when we detect IG / FB /
            TikTok / X / Snapchat / LinkedIn WebView UA. */}
        {inApp && (
          <div
            className="mt-10 pt-6 border-t border-white/10 text-left"
            data-testid="redirect-inapp-hint"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 mb-2">
              // still stuck?
            </div>
            <p className="text-xs text-white/60 leading-relaxed">
              {ios ? (
                <>
                  Tap the <strong className="text-white">⋯</strong>{" "}
                  menu at the top-right and choose{" "}
                  <strong className="text-white">Open in External Browser</strong>{" "}
                  (or <strong className="text-white">Open in Safari</strong>).
                </>
              ) : (
                <>
                  Tap the <strong className="text-white">⋮</strong>{" "}
                  menu and choose{" "}
                  <strong className="text-white">Open in External Browser</strong>{" "}
                  or <strong className="text-white">Open in Chrome</strong>.
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
